import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, BookOpenIcon, SearchIcon, Trash2Icon, FileTextIcon, LayersIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useKnowledgeStore } from '../stores/knowledgeStore';
import { useModelStore } from '../stores/modelStore';
import { useUiStore } from '../stores/uiStore';
import { KnowledgeBase, KnowledgeVisibility } from '../types';
import { PageContainer, PageHeader, EmptyState, KnowledgeBaseStatusBadge, KnowledgeVisibilityBadge } from '../components/common/page';
import { Card, Button, Badge, Input, Select, Label, Textarea } from '../components/common/ui';
import { Modal } from '../components/common/Modal';
import { formatRelative } from '../utils/format';
export function KnowledgeBaseCenterPage() {
  useScreenInit();
  const navigate = useNavigate();
  const knowledgeBases = useKnowledgeStore(s => s.knowledgeBases);
  const {
    toggleKnowledgeBase,
    deleteKnowledgeBase
  } = useKnowledgeStore();
  const pushToast = useUiStore(s => s.pushToast);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const filtered = knowledgeBases.filter(kb => {
    if (statusFilter !== 'all' && kb.status !== statusFilter) return false;
    if (query && !`${kb.name}${kb.description}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const handleDelete = (kb: KnowledgeBase) => {
    if (!deleteKnowledgeBase(kb.knowledgeBaseId)) {
      pushToast('error', `「${kb.name}」被 ${kb.usedByAgentCount} 个 Agent 引用，请先解除绑定`);
    } else {
      pushToast('success', '知识库已删除');
    }
  };
  return <PageContainer data-id="element-575">
      <PageHeader title="知识库中心" description="创建知识库、导入文档、构建索引并供 Agent 绑定使用" action={<Button onClick={() => setCreateOpen(true)} data-id="element-577">
            <PlusIcon className="h-4 w-4" data-id="element-578" /> 创建知识库
          </Button>} data-id="element-576" />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row" data-id="element-579">
        <div className="relative flex-1" data-id="element-580">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-581" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="搜索知识库名称或简介" className="pl-9" data-id="element-582" />
        </div>
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="sm:w-40" data-id="element-583">
          <option value="all" data-id="element-584">全部状态</option>
          <option value="ready" data-id="element-585">就绪</option>
          <option value="indexing" data-id="element-586">索引中</option>
          <option value="failed" data-id="element-587">失败</option>
          <option value="disabled" data-id="element-588">已禁用</option>
        </Select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={BookOpenIcon} title="暂无知识库" description="创建第一个知识库并导入业务文档" data-id="element-589" /> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-590">
          {filtered.map(kb => <Card key={kb.knowledgeBaseId} className="flex flex-col p-4" data-id="element-591">
              <div className="flex items-start justify-between gap-2" data-id="element-592">
                <button onClick={() => navigate(`/knowledge-bases/${kb.knowledgeBaseId}`)} className="flex items-center gap-2.5 text-left" data-id="element-593">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/15" data-id="element-594">
                    <BookOpenIcon className="h-5 w-5 text-emerald-400" data-id="element-595" />
                  </div>
                  <div data-id="element-596">
                    <div className="font-medium text-slate-100 hover:text-indigo-300" data-id="element-597">
                      {kb.name}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-598">
                      {formatRelative(kb.updatedAt)}
                    </div>
                  </div>
                </button>
                <KnowledgeBaseStatusBadge status={kb.status} data-id="element-599" />
              </div>
              <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-400" data-id="element-600">
                {kb.description}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-601">
                <KnowledgeVisibilityBadge visibility={kb.visibility} data-id="element-602" />
                {kb.tags.slice(0, 2).map(t => <Badge key={t} tone="slate" data-id="element-603">
                    {t}
                  </Badge>)}
              </div>
              <div className="mt-3 flex items-center gap-4 border-t border-slate-800 pt-3 text-xs text-slate-500" data-id="element-604">
                <span className="inline-flex items-center gap-1" data-id="element-605">
                  <FileTextIcon className="h-3 w-3" data-id="element-606" /> {kb.documentCount} 文档
                </span>
                <span className="inline-flex items-center gap-1" data-id="element-607">
                  <LayersIcon className="h-3 w-3" data-id="element-608" /> {kb.chunkCount} chunks
                </span>
                <span data-id="element-609">被 {kb.usedByAgentCount} 个 Agent 引用</span>
              </div>
              <div className="mt-3 flex gap-2" data-id="element-610">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate(`/knowledge-bases/${kb.knowledgeBaseId}`)} data-id="element-611">
                  管理
                </Button>
                <Button variant="outline" size="sm" onClick={() => toggleKnowledgeBase(kb.knowledgeBaseId)} data-id="element-612">
                  {kb.status === 'disabled' ? '启用' : '禁用'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(kb)} className="text-red-400 hover:text-red-300" aria-label="删除" data-id="element-613">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-614" />
                </Button>
              </div>
            </Card>)}
        </div>}

      <CreateKbModal open={createOpen} onClose={() => setCreateOpen(false)} data-id="element-615" />
    </PageContainer>;
}
function CreateKbModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createKnowledgeBase = useKnowledgeStore(s => s.createKnowledgeBase);
  const models = useModelStore(s => s.models.filter(m => m.enabled));
  const pushToast = useUiStore(s => s.pushToast);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<KnowledgeVisibility>('team');
  const [embeddingModelId, setEmbeddingModelId] = useState(models[0]?.modelId || '');
  const [topK, setTopK] = useState(5);
  const [scoreThreshold, setScoreThreshold] = useState(0.7);
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setVisibility('team');
      setEmbeddingModelId(models[0]?.modelId || '');
      setTopK(5);
      setScoreThreshold(0.7);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = () => {
    if (!name.trim()) return;
    createKnowledgeBase({
      name: name.trim(),
      description: description.trim(),
      tags: [],
      visibility,
      embeddingModelId,
      retrievalConfig: {
        topK,
        scoreThreshold
      }
    });
    pushToast('info', '知识库创建中，正在初始化索引');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="创建知识库" size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-617">
            取消
          </Button>
          <Button onClick={submit} disabled={!name.trim()} data-id="element-618">
            创建
          </Button>
        </>} data-id="element-616">
      <div className="space-y-4" data-id="element-619">
        <div data-id="element-620">
          <Label required data-id="element-621">名称</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="例如 产品文档库" data-id="element-622" />
        </div>
        <div data-id="element-623">
          <Label data-id="element-624">简介</Label>
          <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="知识库用途说明" data-id="element-625" />
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-626">
          <div data-id="element-627">
            <Label data-id="element-628">可见范围</Label>
            <Select value={visibility} onChange={e => setVisibility(e.target.value as KnowledgeVisibility)} data-id="element-629">
              <option value="private" data-id="element-630">私有</option>
              <option value="team" data-id="element-631">团队</option>
              <option value="public" data-id="element-632">公开</option>
            </Select>
          </div>
          <div data-id="element-633">
            <Label data-id="element-634">Embedding 模型</Label>
            <Select value={embeddingModelId} onChange={e => setEmbeddingModelId(e.target.value)} data-id="element-635">
              {models.map(m => <option key={m.modelId} value={m.modelId} data-id="element-636">
                  {m.name}
                </option>)}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-637">
          <div data-id="element-638">
            <Label data-id="element-639">默认 topK</Label>
            <Input type="number" value={topK} onChange={e => setTopK(Number(e.target.value))} data-id="element-640" />
          </div>
          <div data-id="element-641">
            <Label data-id="element-642">scoreThreshold</Label>
            <Input type="number" step="0.05" value={scoreThreshold} onChange={e => setScoreThreshold(Number(e.target.value))} data-id="element-643" />
          </div>
        </div>
      </div>
    </Modal>;
}